import { Button, ButtonGroup, Card, CircularProgress, Grid, Hidden, Typography } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getVehicleInspectionsStatusList } from '../../Service/vehicleInspectionByStatus-service';

// Inspections summary. Same shape as the Overdue WIPs / Returns / Part Requisitions
// cards: a toggle row across the top (here the schedule events - MOT, DOT 6 Weekly,
// ...) and the per status counts underneath.
export default function Inspections({ state, header = 'Inspections', selectedBranch, handleChangeByBranch }) {
    const history = useHistory();
    const scheduleEvents = state.scheduleEvents || [];
    const rawInspectionCount = state.inspectionCount || [];
    const activeBranch = selectedBranch || state.selectedBranch || 'Default';

    // Filter inspection records by the currently selected branch
    const inspectionCount = useMemo(() => {
        if (!activeBranch || activeBranch === 'Default' || activeBranch === 'All Branches') {
            return rawInspectionCount;
        }
        return rawInspectionCount.filter((c) => {
            const branchName = c.bn || c.branchShortName || c.branchName || c.BranchShortName || c.BranchName || c.Bn;
            if (!branchName) return true;
            return String(branchName).toLowerCase().trim() === String(activeBranch).toLowerCase().trim();
        });
    }, [rawInspectionCount, activeBranch]);

    const ALL = 'ALL';
    // Default to the aggregated "All" view.
    const [selectedEventId, setSelectedEventId] = useState(ALL);

    // Total per event, used both for the count on the button and to decide which
    // events are worth showing at all.
    const totalByEvent = useMemo(() => {
        const m = {};
        inspectionCount.forEach((c) => {
            const id = c.scheduleEventID !== undefined ? c.scheduleEventID : c.id;
            if (id != null) {
                m[id] = (m[id] || 0) + (c.totalCount || 0);
            }
        });
        return m;
    }, [inspectionCount]);

    // Only surface events that actually have inspections against them - the full
    // list is mostly empty and just adds noise.
    const eventsWithData = useMemo(() => {
        const eventsMap = new Map();
        scheduleEvents.forEach((e) => {
            const id = e.scheduleEventID !== undefined ? e.scheduleEventID : e.id;
            const name = e.name || e.scheduleEventName || '';
            if (id != null) {
                eventsMap.set(String(id), { scheduleEventID: id, name });
            }
        });
        inspectionCount.forEach((c) => {
            const id = c.scheduleEventID !== undefined ? c.scheduleEventID : c.id;
            const name = c.scheduleEventName || c.eventName || c.name || '';
            if (id != null) {
                const strId = String(id);
                if (!eventsMap.has(strId)) {
                    eventsMap.set(strId, { scheduleEventID: id, name: name || `Event ${id}` });
                } else if (name && !eventsMap.get(strId).name) {
                    eventsMap.get(strId).name = name;
                }
            }
        });
        return Array.from(eventsMap.values()).filter((e) => (totalByEvent[e.scheduleEventID] || 0) > 0);
    }, [scheduleEvents, inspectionCount, totalByEvent]);

    // Fall back to the first event with data if nothing is picked yet, or if the
    // previously selected event no longer has any (data refreshes on an interval).
    // "All" is always valid and aggregates every event.
    const activeId = useMemo(() => {
        if (selectedEventId === ALL) return ALL;
        const stillValid = selectedEventId != null && eventsWithData.some((e) => String(e.scheduleEventID) === String(selectedEventId));
        return stillValid ? selectedEventId : eventsWithData[0]?.scheduleEventID;
    }, [selectedEventId, eventsWithData]);

    // Currently selected event (undefined when "All"), used to carry the event
    // through to the status screen's Schedule Event column.
    const activeEvent = useMemo(
        () => (activeId === ALL ? null : eventsWithData.find((e) => String(e.scheduleEventID) === String(activeId))),
        [activeId, eventsWithData]
    );

    // Total across every event, for the "All" button badge.
    const totalAll = useMemo(() => Object.values(totalByEvent).reduce((a, b) => a + b, 0), [totalByEvent]);

    // Open the Vehicle Inspection Status screen on the matching status tab, carrying
    // the selected event ID so the grid api filters by ScheduleEventID.
    const goToStatus = (statusId) => {
        if (statusId == null) return;
        let url = `/VehicleInspectionStatus?statusId=${statusId}`;
        const eventId = activeEvent?.scheduleEventID !== undefined ? activeEvent?.scheduleEventID : activeId !== ALL ? activeId : null;
        if (activeId !== ALL && eventId != null) {
            url += `&scheduleEventID=${encodeURIComponent(eventId)}`;
        }
        history.push(url);
    };

    // Columns come from the inspection status master list so every status is always
    // shown, in its proper order, whether or not the selected event has rows for it.
    const [statusList, setStatusList] = useState([]);
    useEffect(() => {
        let cancelled = false;
        getVehicleInspectionsStatusList()
            .then((res) => {
                if (!cancelled) setStatusList(res?.data?.list || []);
            })
            .catch((err) => console.error('Failed to load inspection status list:', err));
        return () => {
            cancelled = true;
        };
    }, []);

    // Counts for the selected event, keyed by statusID. Anything the API returns
    // without a recognised statusID (e.g. "Pending", statusID null) is appended so
    // no data is hidden.
    const { columns, countsById, extraColumns } = useMemo(() => {
        const rows = activeId === ALL ? inspectionCount : inspectionCount.filter((c) => String(c.scheduleEventID) === String(activeId));

        const byId = {};
        rows.forEach((c) => {
            if (c.statusID != null) byId[c.statusID] = (byId[c.statusID] || 0) + (c.totalCount || 0);
        });

        // Colour per status, taken from whatever the API sent for it.
        const colourById = {};
        inspectionCount.forEach((c) => {
            if (c.statusID != null && !colourById[c.statusID]) colourById[c.statusID] = c.color;
        });

        const cols = statusList.map((s) => ({ id: s.id, statusName: s.name, color: colourById[s.id] || 'grey' }));

        const knownIds = new Set(statusList.map((s) => s.id));
        const extras = [];
        const extraSeen = new Map();
        rows.forEach((c) => {
            if (c.statusID == null || !knownIds.has(c.statusID)) {
                const key = c.statusName || 'Unknown';
                extraSeen.set(key, (extraSeen.get(key) || 0) + (c.totalCount || 0));
                if (!extras.some((e) => e.statusName === key)) extras.push({ statusName: key, color: c.color || 'grey' });
            }
        });
        extras.forEach((e) => (e.count = extraSeen.get(e.statusName) || 0));

        return { columns: cols, countsById: byId, extraColumns: extras };
    }, [inspectionCount, activeId, statusList]);

    if (state.showTechnicianLoader) {
        return (
            <div className="wip-dash-Card">
                <div className="pieChartSize" style={{ display: 'flex' }}>
                    <CircularProgress className="wip-loader" />
                </div>
            </div>
        );
    }

    const currentInspectionTotal = activeId === ALL ? totalAll : totalByEvent[activeId] !== undefined ? totalByEvent[activeId] : totalAll;

    return (
        <Card className="wip-dash-Card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: '12px' }}>
                <Typography variant="h6" className="dashHeading">
                    {header}
                </Typography>

                {/* Desktop: Schedule Event Filter Pills */}
                <Hidden only={['xs']}>
                    <div className="inspection-events-container">
                        {eventsWithData.length === 0 && (
                            <span className="lableFont" style={{ color: '#94a3b8', fontSize: '11px' }}>
                                No inspections found
                            </span>
                        )}
                        {eventsWithData.length > 0 && (
                            <button
                                type="button"
                                className={`inspection-event-pill ${activeId === ALL ? 'active' : ''}`}
                                onClick={() => setSelectedEventId(ALL)}
                            >
                                <span className="inspection-event-name">All</span>
                                <span className="inspection-event-count">{totalAll || 0}</span>
                            </button>
                        )}
                        {eventsWithData.map((n) => (
                            <button
                                type="button"
                                key={n.scheduleEventID}
                                className={`inspection-event-pill ${String(activeId) === String(n.scheduleEventID) ? 'active' : ''}`}
                                onClick={() => setSelectedEventId(n.scheduleEventID)}
                            >
                                <span className="inspection-event-name">{n.name || n.scheduleEventName}</span>
                                <span className="inspection-event-count">{totalByEvent[n.scheduleEventID] || 0}</span>
                            </button>
                        ))}
                    </div>
                </Hidden>

                {/* Mobile: Schedule Event Dropdown */}
                <Hidden only={['sm', 'md', 'lg', 'xl']}>
                    {eventsWithData.length > 0 && (
                        <select className="selectclass" value={activeId} onChange={(e) => setSelectedEventId(e.target.value)} style={{ maxWidth: '180px' }}>
                            <option value={ALL}>{`All (${totalAll || 0})`}</option>
                            {eventsWithData.map((n) => (
                                <option key={n.scheduleEventID} value={n.scheduleEventID}>
                                    {n.name || n.scheduleEventName} {totalByEvent[n.scheduleEventID] ? `(${totalByEvent[n.scheduleEventID]})` : ''}
                                </option>
                            ))}
                        </select>
                    )}
                </Hidden>
            </div>

            <div className="dash-stat-tiles-row inspections-grid">
                {columns.length === 0 && extraColumns.length === 0 && (
                    <div style={{ width: '100%' }}>
                        <span className="lableFont" style={{ color: '#94a3b8' }}>
                            No inspection data
                        </span>
                    </div>
                )}
                {/* "All" status - total across every status for the selected event.
                    Opens the status grid on its "All" tab (statusId 0). */}
                {(columns.length > 0 || extraColumns.length > 0) && (
                    <div className="dash-stat-tile-col" key="all-status" onClick={() => goToStatus(0)}>
                        <div className="dash-stat-tile" title="All">
                            <span className="lableFont">All</span>
                            <div className="valueFont">{currentInspectionTotal || 0}</div>
                        </div>
                    </div>
                )}
                {columns.map((s) => (
                    <div className="dash-stat-tile-col" key={s.id} onClick={() => goToStatus(s.id)}>
                        <div
                            // style={{
                            //     borderLeft: `6px solid ${s.color || '#94a3b8'}`
                            // }}
                            className="dash-stat-tile"
                            title={s.statusName}
                        >
                            <span className="lableFont">{s.statusName}</span>
                            <div className="valueFont">{countsById[s.id] || 0}</div>
                        </div>
                    </div>
                ))}
                {extraColumns.map((s) => (
                    <div className="dash-stat-tile-col" key={s.statusName} onClick={() => s.id != null && goToStatus(s.id)}>
                        <div
                            // style={{
                            //     borderLeft: `6px solid ${s.color || '#94a3b8'}`,
                            //     cursor: s.id != null ? 'pointer' : 'default'
                            // }}
                            className="dash-stat-tile"
                            title={s.statusName}
                        >
                            <span className="lableFont">{s.statusName}</span>
                            <div className="valueFont">{s.count || 0}</div>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}
